import React, { useEffect, useState, useMemo } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import * as THREE from "three";

export interface ExistingFile {
  name: string;
  url: string;
}

interface ModelViewerProps {
  files: (File | ExistingFile)[];
  onDelete: (index: number) => void;
  scanUrl?: string;
}

interface FileObjectPair {
  file: File | ExistingFile;
  object: THREE.Object3D | null;
  isLoading: boolean;
  isImage: boolean;
  imageUrl?: string;
  visible: boolean;
}

const UPPER_COLOR = "#fff";
const LOWER_COLOR = "#fff";


const FitCamera: React.FC<{ objects: THREE.Object3D[] }> = ({ objects }) => {
  const { camera, controls } = useThree();

  useEffect(() => {
    if (!objects.length) return;

    const box = new THREE.Box3();
    objects.forEach((obj) => box.expandByObject(obj));

    const center = new THREE.Vector3();
    box.getCenter(center);

    const size = new THREE.Vector3();
    box.getSize(size);

    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = (camera as THREE.PerspectiveCamera).fov * (Math.PI / 180);
    let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));

    camera.position.set(center.x, center.y, cameraZ * 1.5);
    camera.lookAt(center);
    (camera as THREE.PerspectiveCamera).updateProjectionMatrix();

    if (controls) {
      (controls as any).target.copy(center);
      (controls as any).update();
    }
  }, []);

  return null;
};

const ModelViewer: React.FC<ModelViewerProps> = ({ files, onDelete,scanUrl }) => {
  const [fileObjects, setFileObjects] = useState<FileObjectPair[]>([]);

  useEffect(() => {
    if (!files || files.length === 0) {
      setFileObjects([]);
      return;
    }

    const initialFileObjects: FileObjectPair[] = files.map((file) => {
      const fileName = file instanceof File ? file.name : file.name;
      const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName);
      return {
        file,
        object: null,
        isLoading: !isImage,
        isImage,
        visible: true,
        imageUrl:
          isImage && file instanceof File
            ? URL.createObjectURL(file)
            : isImage && !(file instanceof File)
              ? (file as ExistingFile).url
              : undefined,
      };
    });

    setFileObjects(initialFileObjects);

    const loaders = {
      stl: new STLLoader(),
      obj: new OBJLoader(),
    };

    const load3DModel = (
      data: ArrayBuffer | string,
      fileName: string,
      idx: number
    ) => {
      let object: THREE.Object3D | null = null;
      try {
        if (fileName.toLowerCase().endsWith(".stl") && data instanceof ArrayBuffer) {
          const geometry = loaders.stl.parse(data);
          geometry.computeVertexNormals();
          geometry.computeBoundingBox();
          const box = geometry.boundingBox!;
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          const scale = 5 / Math.max(size.x, size.y, size.z);

          geometry.translate(-center.x, -center.y, -center.z);
          geometry.scale(scale, scale, scale);

          object = new THREE.Mesh(
            geometry,
            new THREE.MeshStandardMaterial({ color: "#cccccc" })
          );


          (object as any).fileName = fileName;

        } else if (fileName.toLowerCase().endsWith(".obj") && typeof data === "string") {
          object = loaders.obj.parse(data);

          object.traverse((child: any) => {
            if (child.isMesh)
              child.material = new THREE.MeshStandardMaterial({
                color: "#cccccc",
              });
          });

          const box = new THREE.Box3().setFromObject(object);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          const scale = 5 / Math.max(size.x, size.y, size.z);

          object.position.set(-center.x * scale, -center.y * scale, -center.z * scale);
          object.scale.set(scale, scale, scale);

          (object as any).fileName = fileName;
        }
      } catch (err) {
        console.warn("Error parsing file:", err);
      }

      setFileObjects((prev) =>
        prev.map((f, i) =>
          i === idx ? { ...f, object, isLoading: false } : f
        )
      );
    };

    files.forEach((file, idx) => {
      const fileName = file instanceof File ? file.name : file.name;
      const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName);
      if (isImage) return;

      if (file instanceof File) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const contents = e.target?.result;
          if (!contents) return;
          load3DModel(contents, fileName, idx);
        };
        if (fileName.toLowerCase().endsWith(".stl"))
          reader.readAsArrayBuffer(file);
        else if (fileName.toLowerCase().endsWith(".obj"))
          reader.readAsText(file);
      } else {
        const fileUrl = (file as ExistingFile).url;
        fetch(fileUrl)
          .then(async (res) => {
            let data: ArrayBuffer | string;
            if (fileUrl.toLowerCase().endsWith(".stl")) {
              data = await res.arrayBuffer();
            } else {
              data = await res.text();
            }
            return data;
          })
          .then((data: ArrayBuffer | string) => {
            load3DModel(data, fileName, idx);
          });
      }
    });
  }, [files]);


  useEffect(() => {
    const loadedObjects = fileObjects.filter(f => f.object && !f.isLoading && !f.isImage);

    if (loadedObjects.length === 1) {
      const obj = loadedObjects[0].object!;
      const fileName = (obj as any).fileName?.toLowerCase() || '';


      if (fileName.includes('upper')) {
        obj.position.y = 1.0;
        obj.traverse((child: any) => {
          if (child.isMesh) child.material.color.set(UPPER_COLOR);
        });
      } else if (fileName.includes('lower')) {
        obj.position.y = -1.0;
        obj.traverse((child: any) => {
          if (child.isMesh) child.material.color.set(LOWER_COLOR);
        });
      }

    } else if (loadedObjects.length === 2) {
      const [obj1, obj2] = loadedObjects.map(f => f.object!);
      const fileName1 = (obj1 as any).fileName?.toLowerCase() || '';
      const fileName2 = (obj2 as any).fileName?.toLowerCase() || '';


      if (fileName1.includes('upper') || !fileName2.includes('upper')) {

        obj1.position.y = 0.35;
        obj2.position.y = -0.35;

        obj1.traverse((child: any) => {
          if (child.isMesh) child.material.color.set(UPPER_COLOR);
        });
        obj2.traverse((child: any) => {
          if (child.isMesh) child.material.color.set(LOWER_COLOR);
        });
      } else {

        obj2.position.y = 0.36;
        obj1.position.y = -0.36;

        obj2.traverse((child: any) => {
          if (child.isMesh) child.material.color.set(UPPER_COLOR);
        });
        obj1.traverse((child: any) => {
          if (child.isMesh) child.material.color.set(LOWER_COLOR);
        });
      }
    }
  }, [fileObjects]);


  const toggleVisibility = (index: number) => {
    setFileObjects(prev =>
      prev.map((f, i) =>
        i === index ? { ...f, visible: !f.visible } : f
      )
    );
  };

  const fileTextures = useMemo(() => {
    return fileObjects.map((f) => {
      if (f.isImage && f.imageUrl) {
        const tex = new THREE.TextureLoader().load(f.imageUrl);
        tex.needsUpdate = true;
        tex.magFilter = THREE.LinearFilter;
        tex.minFilter = THREE.LinearFilter;
        return tex;
      }
      return null;
    });
  }, [fileObjects]);


  const visibleObjects = useMemo(() => {
    return fileObjects
      .filter(f => f.visible && f.object && !f.isImage)
      .map(f => f.object) as THREE.Object3D[];
  }, [fileObjects]);

  return (
    <div className="viewer-container mb-3">
      <div className="row">
        <div className="col-md-8">
          <div className="viewer-preview">
            <Canvas
              camera={{ position: [1, 1, 1], fov: 50 }}
              className="canvas-section"
            >
              <color attach="background" args={["#000000"]} />
              <ambientLight intensity={0.6} />
              <directionalLight position={[10, 10, 5]} intensity={1} />
              <pointLight position={[-10, -10, -5]} intensity={0.3} />
              <OrbitControls enableDamping dampingFactor={0.05} minDistance={0} maxDistance={50} />


              {visibleObjects.length > 0 && <FitCamera objects={visibleObjects} />}

              {fileObjects.map((fileObj, idx) => {
                if (!fileObj.visible) return null;

                if (fileObj.isImage && fileTextures[idx]) {
                  return (
                    <mesh key={`image-${idx}`} position={[0, 0, 0]}>
                      <planeGeometry args={[7.2, 4.8]} />
                      <meshBasicMaterial
                        map={fileTextures[idx]}
                        transparent={true}
                        alphaTest={0.99}
                      />
                    </mesh>
                  );
                }
                if (fileObj.object) {
                  return <primitive key={`object-${idx}`} object={fileObj.object} />;
                }
                return null;
              })}
            </Canvas>

            {files.length === 0 && (
              <div className="empty-preview">
                <p>No files uploaded yet</p>
                <p>Upload STL, OBJ, or Image files to preview them here</p>
              </div>
            )}
          </div>
        </div>
        <div className="col-md-4">
          <div className="viewer-layers mb-3">
            <h4 className="mt-3 scans-border-bottom">Scans ({files.length})</h4>


            {fileObjects.map((fileObj, index) => (
              <div key={index} className="file-preview">

                <div className="visibility-toggle">
              

                  <span className="file-name">
                    {fileObj.file instanceof File ? (
                      fileObj.file.name
                    ) : (
                      <a
                        href={(fileObj.file as ExistingFile).url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {(fileObj.file as ExistingFile).name}
                      </a>
                    )}
                  </span>

                  {fileObj.isLoading && <span className="loading-text">Loading...</span>}
                </div>
              </div>
            ))}
          </div>
          {/* <a href="#" className="open-scan-file">Open Scan File</a> */}
          {scanUrl && (
  <a
    href={scanUrl}
    target="_blank"
    rel="noopener noreferrer"
    className="open-scan-file"
  >
    Open Scan File
  </a>
)}
        </div>
      </div>
      <style>{`
        .viewer-container { 
      
            padding-left: 15px;
    padding-right: 15px;
}
 .scans-border-bottom{
     border-bottom: 1px solid #0000005e;
    padding-top: 10px;
 }      
      
      
       
        }
        .viewer-layers { 
        
          padding-right: 15px; 
        }
        .viewer-layers h4 { 
          margin-top: 0; 
          font-size: 16px; 
          color: #333; 
              padding-bottom: 10px;
         
        }
    

        .file-preview {   
  box-shadow: rgba(17, 17, 26, 0.05) 0px 1px 0px,
              rgba(17, 17, 26, 0.1) 0px 0px 8px; 
  display: flex; 
  flex-direction: column; /* 👈 important change */
  justify-content: space-between; 
  margin-bottom: 12px; 
  padding: 8px; 
  background: #F8F9FB; 
  border-radius: 4px; 
  border: 1px solid #e9ecef; 
}
        .file-info {
          display: flex;
          flex-direction: column;
          flex: 1;
        }
        .visibility-toggle {
          display: flex;
          align-items: center;

          gap: 8px;
          cursor: pointer;
        }
        .file-name {
          font-size: 13px; 
          font-weight: 500; 
          color: #495057; 
          text-decoration: none; 
          word-break: break-all;
             overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
      width: 200px;
        }
        .file-name a {
          color: #495057;
          text-decoration: none;
        }
        .file-name a:hover {
          text-decoration: underline;
        }
        .loading-text {
          font-size: 11px;
          color: #6c757d;
          font-style: italic;
        }
     



        .delete-btn {
  background: #dc3545;

  padding: 6px 12px;

  color: #ffff;
  border: 1px solid #dc3545;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: 0.2s;
}
.delete-btn:hover {
  background: #dc3545;
  color: #fff;
}
        .viewer-preview { 
          flex: 1; 
          height: 400px; 
          
          border-radius: 6px; 
         
          background: #fafafa; 
          position: relative; 
        }
        .canvas-section {
          background-color: #fafafa;
             margin-top:20px;
                }
        .empty-preview { 
          position: absolute; 
          top: 50%; 
          left: 50%; 
          transform: translate(-50%, -50%); 
          text-align: center; 
          color: #6c757d; 
        }
      `}</style>
    </div>
  );
};

export default ModelViewer;